import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';

const API = window.location.origin;

const MeetingScheduler = ({ isOpen, onClose, cardId, candidateId, candidateName, candidateEmail, pipelineName, pipelineId, onMeetingCreated, prefill, embedded = false, openFormSignal = 0 }) => {
  const [connections, setConnections] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [resolvedCandidateEmail, setResolvedCandidateEmail] = useState(candidateEmail || null);
  const [busyTimes, setBusyTimes] = useState([]);
  const [checkingBusy, setCheckingBusy] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [presFiles, setPresFiles] = useState([]);
  const [meta, setMeta] = useState({});
  const [templates, setTemplates] = useState([]);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ label: '', title_pattern: '', description_pattern: '', location_type: 'virtual', duration_minutes: 60, attach_files: false, default_location: '' });
  const [confirmingSendId, setConfirmingSendId] = useState(null);
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();

  const [form, setForm] = useState({
    title: `Interview: ${candidateName || 'Candidate'} — ${pipelineName || 'Search'}`,
    description: '',
    date: '',
    startTime: '',
    endTime: '',
    locationType: 'virtual',
    location: '',
    attendees: []
  });

  const [customEmail, setCustomEmail] = useState('');
  const [templateDuration, setTemplateDuration] = useState(60);

  // Variable substitution for templates
  const vars = {
    candidate: candidateName || 'Candidate',
    pipeline: pipelineName || meta.pipeline_name || 'Search',
    client_firm: meta.client_firm_name || 'Client',
    recruiter_firm: meta.recruiter_firm_name || 'Firm',
    organizer: user.name || 'Recruiter'
  };

  const substituteVars = (str, extraVars) => {
    if (!str) return '';
    const v = { ...vars, ...extraVars };
    return str.replace(/\{candidate\}/g, v.candidate)
      .replace(/\{pipeline\}/g, v.pipeline)
      .replace(/\{client_firm\}/g, v.client_firm)
      .replace(/\{recruiter_firm\}/g, v.recruiter_firm)
      .replace(/\{organizer\}/g, v.organizer)
      .replace(/\{location\}/g, v.location || '');
  };

  const applyTemplate = (t) => {
    const dur = t.duration_minutes || 60;
    setTemplateDuration(dur);
    const extra = { location: t.default_location || '' };

    let desc = substituteVars(t.description_pattern, extra);
    if (t.attach_files && presFiles.length > 0) {
      desc += '\n\n📎 Attached Files:';
      presFiles.forEach(f => {
        desc += `\n• ${f.filename}`;
      });
      desc += `\n\nView file(s): ${window.location.origin}/search/${pipelineId}?tab=pipeline&card=${cardId}&panel=review`;
    }

    setForm(prev => {
      const updates = {
        ...prev,
        title: substituteVars(t.title_pattern, extra),
        description: desc,
        locationType: t.location_type || 'virtual',
        location: t.default_location || ''
      };
      // Auto-calc end time if start time already set
      if (prev.startTime) {
        const [h, m] = prev.startTime.split(':').map(Number);
        const endMin = h * 60 + m + dur;
        updates.endTime = `${String(Math.floor(endMin / 60) % 24).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
      }
      return updates;
    });
  };

  // Auto-calculate end time when start time changes
  useEffect(() => {
    if (!form.startTime) return;
    const [h, m] = form.startTime.split(':').map(Number);
    const endMin = h * 60 + m + templateDuration;
    const newEnd = `${String(Math.floor(endMin / 60) % 24).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
    setForm(prev => ({ ...prev, endTime: newEnd }));
  }, [form.startTime]);

  const saveTemplate = async () => {
    try {
      const isEdit = !!editingTemplate;
      const url = isEdit ? `${API}/api/calendar/templates/${editingTemplate.id}` : `${API}/api/calendar/templates`;
      const resp = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST', headers,
        body: JSON.stringify(templateForm)
      });
      if (resp.ok) {
        const saved = await resp.json();
        if (isEdit) {
          setTemplates(prev => prev.map(t => t.id === saved.id ? saved : t));
        } else {
          setTemplates(prev => [...prev, saved]);
        }
        setEditingTemplate(null);
        setTemplateForm({ label: '', title_pattern: '', description_pattern: '', location_type: 'virtual', duration_minutes: 60, attach_files: false, default_location: '' });
        setShowTemplateManager(false);
      }
    } catch {}
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await fetch(`${API}/api/calendar/templates/${id}`, { method: 'DELETE', headers });
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch {}
  };

  const startEditTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({
      label: t.label, title_pattern: t.title_pattern, description_pattern: t.description_pattern || '',
      location_type: t.location_type || 'virtual', duration_minutes: t.duration_minutes || 60, attach_files: t.attach_files || false,
      default_location: t.default_location || ''
    });
    setShowTemplateManager(true);
  };

  const startNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ label: '', title_pattern: '', description_pattern: '', location_type: 'virtual', duration_minutes: 60, attach_files: false, default_location: '' });
    setShowTemplateManager(true);
  };
  const candEmail = resolvedCandidateEmail;
  const suggestedAttendees = [
    user.email && { email: user.email, name: user.name || 'You', role: 'Organizer' },
    candEmail && { email: candEmail, name: candidateName || 'Candidate', role: 'Candidate' },
    ...participants.filter(p => p.email !== user.email && p.email !== candEmail).map(p => ({
      email: p.email, name: p.name, role: p.role || p.user_type
    }))
  ].filter(Boolean);

  useEffect(() => {
    if (!isOpen || !cardId) return;
    loadData();
  }, [isOpen, cardId]);

  // When opened with a prefill (e.g. from a candidate's selected availability),
  // pre-populate the form and jump straight to it.
  useEffect(() => {
    if (isOpen && prefill && (prefill.date || prefill.startTime)) {
      setForm(prev => ({
        ...prev,
        date: prefill.date || prev.date,
        startTime: prefill.startTime || prev.startTime,
        endTime: prefill.endTime || prev.endTime,
      }));
      setShowForm(true);
    }
  }, [isOpen, prefill]);

  // External "open the form" signal (from the InterviewsSection + button).
  useEffect(() => {
    if (openFormSignal > 0) setShowForm(true);
  }, [openFormSignal]);

  const loadData = async () => {
    setLoading(true);
    try {
      const fetches = [
        fetch(`${API}/api/calendar/connections`, { headers }),
        fetch(`${API}/api/calendar/meetings/${cardId}`, { headers }),
        pipelineId ? fetch(`${API}/api/calendar/pipeline-participants/${pipelineId}`, { headers }) : Promise.resolve(null),
        fetch(`${API}/api/cards/${cardId}/presentation-files`, { headers }),
        candidateId ? fetch(`${API}/api/client/candidates/${candidateId}/data`, { headers }) : Promise.resolve(null),
        fetch(`${API}/api/calendar/templates`, { headers })
      ];
      const [connResp, meetResp, partResp, filesResp, ccdResp, tmplResp] = await Promise.all(fetches);
      if (connResp.ok) setConnections(await connResp.json());
      if (meetResp.ok) setMeetings(await meetResp.json());
      if (partResp?.ok) {
        const partData = await partResp.json();
        setParticipants(partData.participants || partData);
        if (partData.meta) setMeta(partData.meta);
      }
      if (filesResp.ok) setPresFiles(await filesResp.json());
      // Resolve candidate email from CCD
      if (ccdResp?.ok) {
        const ccd = await ccdResp.json();
        const email = ccd.client_email || (ccd.emails && ccd.emails[0]?.value) || candidateEmail;
        if (email) setResolvedCandidateEmail(email);
      }
      if (tmplResp?.ok) setTemplates(await tmplResp.json());
    } catch (err) { console.error('Load error:', err); }
    finally { setLoading(false); }
  };

  const connectCalendar = async (provider) => {
    try {
      const returnUrl = window.location.pathname;
      const resp = await fetch(`${API}/api/calendar/${provider}/authorize?returnUrl=${encodeURIComponent(returnUrl)}`, { headers });
      const data = await resp.json();
      if (data.url) window.location.href = data.url;
    } catch (err) { console.error('Connect error:', err); }
  };

  const disconnectCalendar = async (provider) => {
    try {
      await fetch(`${API}/api/calendar/connections/${provider}`, { method: 'DELETE', headers });
      setConnections(prev => prev.filter(c => c.provider !== provider));
    } catch {}
  };

  const toggleAttendee = (attendee) => {
    setForm(prev => {
      const exists = prev.attendees.find(a => a.email === attendee.email);
      if (exists) return { ...prev, attendees: prev.attendees.filter(a => a.email !== attendee.email) };
      return { ...prev, attendees: [...prev.attendees, { ...attendee, optional: false }] };
    });
  };

  const toggleOptional = (email) => {
    setForm(prev => ({
      ...prev,
      attendees: prev.attendees.map(a => a.email === email ? { ...a, optional: !a.optional } : a)
    }));
  };

  const addCustomAttendee = () => {
    if (!customEmail || !customEmail.includes('@')) return;
    const attendee = { email: customEmail.trim(), name: customEmail.trim(), role: 'Guest' };
    setForm(prev => ({ ...prev, attendees: [...prev.attendees.filter(a => a.email !== attendee.email), attendee] }));
    setCustomEmail('');
  };

  const removeAttendee = (email) => {
    setForm(prev => ({ ...prev, attendees: prev.attendees.filter(a => a.email !== email) }));
  };

  const checkAvailability = async () => {
    if (!form.date || form.attendees.length === 0) return;
    setCheckingBusy(true);
    try {
      const startISO = new Date(`${form.date}T00:00:00`).toISOString();
      const endISO = new Date(`${form.date}T23:59:59`).toISOString();
      const resp = await fetch(`${API}/api/calendar/free-busy`, {
        method: 'POST', headers,
        body: JSON.stringify({ start: startISO, end: endISO, emails: form.attendees.map(a => a.email) })
      });
      if (resp.ok) {
        const data = await resp.json();
        setBusyTimes(data.busyTimes || []);
      }
    } catch {} finally { setCheckingBusy(false); }
  };

  useEffect(() => { if (form.date && form.attendees.length > 0 && connections.length > 0) checkAvailability(); }, [form.date]);

  const handleSubmit = async () => {
    if (!form.date || !form.startTime || !form.endTime || !form.title) {
      alert('Please fill in date, time, and title');
      return;
    }
    setCreating(true);
    try {
      const startISO = new Date(`${form.date}T${form.startTime}:00`).toISOString();
      const endISO = new Date(`${form.date}T${form.endTime}:00`).toISOString();
      
      const body = {
        card_id: cardId,
        title: form.title,
        description: form.description,
        start_time: startISO,
        end_time: endISO,
        location: form.locationType === 'in-person' ? form.location : null,
        attendees: form.attendees
      };

      const isEdit = !!editingMeeting;
      const url = isEdit ? `${API}/api/calendar/meetings/${editingMeeting.id}` : `${API}/api/calendar/meetings`;
      const method = isEdit ? 'PUT' : 'POST';

      const resp = await fetch(url, { method, headers, body: JSON.stringify(body) });

      if (resp.ok) {
        const meeting = await resp.json();
        if (isEdit) {
          setMeetings(prev => prev.map(m => m.id === meeting.id ? meeting : m));
        } else {
          setMeetings(prev => [...prev, meeting]);
        }
        setShowForm(false);
        setEditingMeeting(null);
        if (onMeetingCreated) onMeetingCreated(meeting);
      } else {
        const err = await resp.json();
        alert(err.error || 'Failed to save meeting');
      }
    } catch (err) {
      console.error('Save meeting error:', err);
      alert('Failed to save meeting');
    } finally { setCreating(false); }
  };

  const editMeeting = (m) => {
    const start = new Date(m.start_time);
    const end = new Date(m.end_time);
    setForm({
      title: m.title || '',
      description: m.description || '',
      date: start.toISOString().split('T')[0],
      startTime: start.toTimeString().slice(0, 5),
      endTime: end.toTimeString().slice(0, 5),
      locationType: m.location ? 'in-person' : 'virtual',
      location: m.location || '',
      attendees: m.attendees || []
    });
    setEditingMeeting(m);
    setShowForm(true);
  };

  const deleteMeeting = async (meetingId) => {
    if (!window.confirm('Delete this draft meeting?')) return;
    try {
      await fetch(`${API}/api/calendar/meetings/${meetingId}`, { method: 'DELETE', headers });
      setMeetings(prev => prev.filter(m => m.id !== meetingId));
    } catch {}
  };

  const cancelMeeting = async (meetingId) => {
    if (!window.confirm('Cancel this meeting? Invites will be removed from all attendee calendars.')) return;
    try {
      await fetch(`${API}/api/calendar/meetings/${meetingId}/cancel`, { method: 'PUT', headers });
      setMeetings(prev => prev.filter(m => m.id !== meetingId));
    } catch {}
  };

  const sendMeeting = async (meetingId) => {
    setSending(true);
    try {
      const resp = await fetch(`${API}/api/calendar/meetings/${meetingId}/send`, { method: 'PUT', headers });
      if (resp.ok) {
        const updated = await resp.json();
        setMeetings(prev => prev.map(m => m.id === updated.id ? updated : m));
        setConfirmingSendId(null);
      } else {
        const err = await resp.json();
        alert(err.error || 'Failed to send invites');
      }
    } catch { alert('Failed to send invites'); }
    finally { setSending(false); }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };
  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (!isOpen) return null;

  const innerCard = (
      <div className={embedded ? "flex flex-col" : "bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col"} onClick={e => e.stopPropagation()}>
        {/* Header — hidden when embedded (the Interviews section heading covers it) */}
        {!embedded && (
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Schedule Meeting</h3>
            <p className="text-xs text-gray-500 mt-0.5">{candidateName} · {pipelineName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-0.5">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        )}

        <div className={embedded ? "space-y-3" : "flex-1 overflow-y-auto px-5 py-4 space-y-4"}>
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div></div>
          ) : (
            <>
              {/* Existing Meetings — shown FIRST in embedded (top of Interviews) */}
              {meetings.length > 0 && (
                <div className="space-y-1.5">
                  {!embedded && <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Scheduled Meetings</p>}
                  {meetings.map(m => (
                    <div key={m.id} className="flex items-start gap-2 px-2.5 py-1.5 bg-emerald-50/50 border border-emerald-100 rounded group/meet">
                      <svg className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] font-medium text-gray-800 truncate">{m.title}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${m.status === 'sent' ? 'bg-green-100 text-green-700' : m.status === 'cancelled' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-700'}`}>{m.status === 'sent' ? 'Sent' : m.status === 'cancelled' ? 'Cancelled' : 'Draft'}</span>
                        </div>
                        <div className="text-[10px] text-gray-500">{formatDate(m.start_time)} · {formatTime(m.start_time)}–{formatTime(m.end_time)}</div>
                        {confirmingSendId === m.id && (
                          <div className="mt-1.5 pt-1.5 border-t border-emerald-200 flex items-center justify-between">
                            <span className="text-[10px] text-gray-600">Send invites to attendees?</span>
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => setConfirmingSendId(null)} className="text-[10px] text-gray-500 hover:text-gray-700">No</button>
                              <button onClick={() => sendMeeting(m.id)} disabled={sending} className="text-[10px] text-white bg-emerald-600 hover:bg-emerald-700 px-2 py-0.5 rounded font-medium disabled:opacity-50">{sending ? 'Sending…' : 'Confirm'}</button>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Actions on the right to save vertical space */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {m.meeting_url && <a href={m.meeting_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline">Join</a>}
                        <button onClick={() => editMeeting(m)} className="text-[10px] text-gray-500 hover:text-gray-800">Edit</button>
                        {m.status === 'draft' && confirmingSendId !== m.id && (
                          <button onClick={() => setConfirmingSendId(m.id)} className="text-[10px] text-emerald-600 hover:text-emerald-800 font-medium">Send</button>
                        )}
                        {m.status === 'draft' && <button onClick={() => deleteMeeting(m.id)} className="text-[10px] text-red-500 hover:text-red-700">Delete</button>}
                        {m.status === 'sent' && <button onClick={() => cancelMeeting(m.id)} className="text-[10px] text-red-500 hover:text-red-700">Cancel</button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Calendar Connections */}
              <div className="space-y-2">
                {!embedded && <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Calendar Connection</p>}
                {connections.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800">Connect a calendar to auto-create events with video call links.</p>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => connectCalendar('google')}
                        className="px-3 py-1.5 text-xs bg-white border rounded-lg hover:bg-gray-50 flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                        Google Calendar
                      </button>
                      <button onClick={() => connectCalendar('microsoft')}
                        className="px-3 py-1.5 text-xs bg-white border rounded-lg hover:bg-gray-50 flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24"><rect fill="#F25022" x="1" y="1" width="10" height="10"/><rect fill="#7FBA00" x="13" y="1" width="10" height="10"/><rect fill="#00A4EF" x="1" y="13" width="10" height="10"/><rect fill="#FFB900" x="13" y="13" width="10" height="10"/></svg>
                        Outlook
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {connections.map(c => (
                      <div key={c.id} className="flex items-center justify-between px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span className="text-xs font-medium text-green-800">{c.provider === 'google' ? 'Google' : 'Outlook'}</span>
                          <span className="text-xs text-green-600">{c.calendar_email}</span>
                        </div>
                        <button onClick={() => disconnectCalendar(c.provider)} className="text-[10px] text-red-500 hover:text-red-700">Disconnect</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* New Meeting Form — embedded: no internal + button (InterviewsSection drives it) */}
              {!showForm ? (
                !embedded && (
                  <button onClick={() => setShowForm(true)}
                    className="w-full py-2 text-xs font-medium bg-emerald-50 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-100 transition-colors">
                    + Schedule New Meeting
                  </button>
                )
              ) : (
                <div className={embedded ? "space-y-3 pt-1" : "space-y-3 border rounded-lg p-4 bg-gray-50"}>
                  {!embedded && <p className="text-xs font-semibold text-gray-700">{editingMeeting ? 'Edit Meeting' : 'New Meeting'}</p>}

                  {/* Template Selector */}
                  {!editingMeeting && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[10px] text-gray-500">Quick Template</label>
                        <button onClick={startNewTemplate} className="text-[10px] text-blue-600 hover:text-blue-800 font-medium">+ New Template</button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {templates.map(t => (
                          <div key={t.id} className="group relative">
                            <button onClick={() => applyTemplate(t)}
                              className="px-2.5 py-1.5 text-[10px] bg-white border rounded-lg hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors text-gray-600 pr-6">
                              {t.label}
                            </button>
                            <div className="absolute right-0.5 top-0.5 hidden group-hover:flex items-center gap-0.5">
                              <button onClick={(e) => { e.stopPropagation(); startEditTemplate(t); }}
                                className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-blue-600 bg-white rounded">
                                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id); }}
                                className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-red-500 bg-white rounded">
                                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Template Editor */}
                      {showTemplateManager && (
                        <div className="mt-2 p-3 border border-blue-200 rounded-lg bg-blue-50 space-y-2">
                          <p className="text-[10px] font-semibold text-blue-800">{editingTemplate ? 'Edit Template' : 'New Template'}</p>
                          <input type="text" value={templateForm.label} onChange={e => setTemplateForm(prev => ({ ...prev, label: e.target.value }))}
                            className="w-full px-2 py-1.5 text-xs border rounded bg-white" placeholder="Template name (e.g. Client Interview)" />
                          <input type="text" value={templateForm.title_pattern} onChange={e => setTemplateForm(prev => ({ ...prev, title_pattern: e.target.value }))}
                            className="w-full px-2 py-1.5 text-xs border rounded bg-white" placeholder="Title pattern (use {candidate}, {client_firm}, {pipeline})" />
                          <textarea value={templateForm.description_pattern} onChange={e => setTemplateForm(prev => ({ ...prev, description_pattern: e.target.value }))}
                            rows={2} className="w-full px-2 py-1.5 text-xs border rounded bg-white resize-none" placeholder="Description pattern (use {candidate}, {client_firm}, {pipeline}, {recruiter_firm}, {organizer})" />
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                              <label className="text-[10px] text-gray-600">Type:</label>
                              <select value={templateForm.location_type} onChange={e => setTemplateForm(prev => ({ ...prev, location_type: e.target.value }))}
                                className="text-[10px] border rounded px-1.5 py-1 bg-white">
                                <option value="virtual">Video Call</option>
                                <option value="in-person">In-Person</option>
                              </select>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <label className="text-[10px] text-gray-600">Duration:</label>
                              <select value={templateForm.duration_minutes} onChange={e => setTemplateForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) }))}
                                className="text-[10px] border rounded px-1.5 py-1 bg-white">
                                <option value={15}>15 min</option>
                                <option value={30}>30 min</option>
                                <option value={45}>45 min</option>
                                <option value={60}>60 min</option>
                                <option value={90}>90 min</option>
                              </select>
                            </div>
                            <label className="flex items-center gap-1 text-[10px] text-gray-600 cursor-pointer">
                              <input type="checkbox" checked={templateForm.attach_files} onChange={e => setTemplateForm(prev => ({ ...prev, attach_files: e.target.checked }))}
                                className="rounded border-gray-300" />
                              Attach files
                            </label>
                          </div>
                          <input type="text" value={templateForm.default_location} onChange={e => setTemplateForm(prev => ({ ...prev, default_location: e.target.value }))}
                            className="w-full px-2 py-1.5 text-xs border rounded bg-white" placeholder="Default location (e.g. 123 Main St, New York, NY or Google Meet)" />
                          <div className="flex items-center justify-between pt-1">
                            <button onClick={() => { setShowTemplateManager(false); setEditingTemplate(null); }} className="text-[10px] text-gray-500 hover:text-gray-700">Cancel</button>
                            <button onClick={saveTemplate} disabled={!templateForm.label || !templateForm.title_pattern}
                              className="px-3 py-1 text-[10px] font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                              {editingTemplate ? 'Update' : 'Save Template'}
                            </button>
                          </div>
                          <p className="text-[9px] text-blue-600">Variables: {'{candidate}'} {'{pipeline}'} {'{client_firm}'} {'{recruiter_firm}'} {'{organizer}'} {'{location}'}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Title */}
                  <input type="text" value={form.title} onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" placeholder="Meeting title" />

                  {/* Date & Time */}
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] text-gray-500 block mb-0.5">Date</label>
                      <input type="date" value={form.date} onChange={e => setForm(prev => ({ ...prev, date: e.target.value }))}
                        className="w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 block mb-0.5">Start</label>
                      <input type="time" step="300" value={form.startTime} onChange={e => setForm(prev => ({ ...prev, startTime: e.target.value }))}
                        className="w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 block mb-0.5">End</label>
                      <input type="time" step="300" value={form.endTime} onChange={e => setForm(prev => ({ ...prev, endTime: e.target.value }))}
                        className="w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" />
                    </div>
                  </div>

                  {/* Busy Times */}
                  {busyTimes.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                      <p className="text-[10px] font-semibold text-red-700 mb-1">Busy Times on {form.date}:</p>
                      {busyTimes.map((b, i) => (
                        <p key={i} className="text-[10px] text-red-600">{b.email}: {formatTime(b.start)} – {formatTime(b.end)}</p>
                      ))}
                    </div>
                  )}
                  {checkingBusy && <p className="text-[10px] text-gray-400 animate-pulse">Checking availability...</p>}

                  {/* Location Type */}
                  <div>
                    <label className="text-[10px] text-gray-500 block mb-1">Meeting Type</label>
                    <div className="flex gap-2">
                      <button onClick={() => setForm(prev => ({ ...prev, locationType: 'virtual', location: '' }))}
                        className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-colors ${form.locationType === 'virtual' ? 'bg-blue-50 border-blue-300 text-blue-700 font-medium' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                        <span className="block">📹 Video Call</span>
                        <span className="text-[9px] text-gray-400">{connections.length > 0 ? 'Auto-creates link' : 'Connect calendar first'}</span>
                      </button>
                      <button onClick={() => setForm(prev => ({ ...prev, locationType: 'in-person' }))}
                        className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-colors ${form.locationType === 'in-person' ? 'bg-blue-50 border-blue-300 text-blue-700 font-medium' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                        <span className="block">📍 In Person</span>
                        <span className="text-[9px] text-gray-400">Enter address</span>
                      </button>
                    </div>
                  </div>

                  <input type="text" value={form.location} onChange={e => setForm(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" 
                    placeholder={form.locationType === 'in-person' ? 'Enter meeting location...' : 'Location (optional — video link auto-generated)'} />

                  {/* Description */}
                  <div>
                    <label className="text-[10px] text-gray-500 block mb-1">Description / Notes</label>
                    <textarea value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={2} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white resize-none" placeholder="Meeting notes (optional)..." />
                    
                    {/* Attach presentation files to description */}
                    {presFiles.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-[10px] text-gray-500">Attach file links to description:</p>
                        {presFiles.map(f => (
                          <button key={f.id} onClick={() => {
                            const fileNote = `\n📎 ${f.filename}`;
                            setForm(p => {
                              const hasViewLink = (p.description || '').includes('View file(s):');
                              const viewLink = hasViewLink ? '' : `\n\nView file(s): ${window.location.origin}/search/${pipelineId}?tab=pipeline&card=${cardId}&panel=review`;
                              return { ...p, description: (p.description || '') + fileNote + viewLink };
                            });
                          }}
                            className="flex items-center gap-1.5 px-2 py-1 text-[10px] bg-white border rounded hover:bg-blue-50 hover:border-blue-300 transition-colors">
                            <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            <span className="text-gray-700">{f.filename}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Attendees */}
                  <div className="space-y-2">
                    <label className="text-[10px] text-gray-500 block">Attendees</label>
                    
                    {/* Suggested attendees */}
                    <div className="space-y-1">
                      {suggestedAttendees.map(a => {
                        const added = form.attendees.find(att => att.email === a.email);
                        const isAdded = !!added;
                        return (
                          <div key={a.email} className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-colors ${isAdded ? 'bg-blue-50 border-blue-300' : 'bg-white hover:bg-gray-50'}`}>
                            <button onClick={() => toggleAttendee(a)} className="flex items-center gap-2 text-left flex-1 min-w-0">
                              {isAdded ? (
                                <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                              ) : (
                                <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth={2} /></svg>
                              )}
                              <div className="min-w-0">
                                <span className="text-xs font-medium text-gray-900">{a.name}</span>
                                <span className="text-[10px] text-gray-500 ml-1.5 truncate">{a.email}</span>
                              </div>
                            </button>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                                a.role === 'Organizer' ? 'bg-purple-100 text-purple-700' :
                                a.role === 'Candidate' ? 'bg-green-100 text-green-700' :
                                a.role === 'Client' ? 'bg-orange-100 text-orange-700' :
                                a.role === 'Recruiter' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>{a.role}</span>
                              {isAdded && (
                                <button onClick={(e) => { e.stopPropagation(); toggleOptional(a.email); }}
                                  className={`text-[9px] px-1.5 py-0.5 rounded border transition-colors ${added.optional ? 'bg-yellow-50 border-yellow-300 text-yellow-700' : 'bg-blue-100 border-blue-300 text-blue-700'}`}>
                                  {added.optional ? 'Optional' : 'Required'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Custom attendee rows */}
                    {form.attendees.filter(a => !suggestedAttendees.some(s => s.email === a.email)).map(a => (
                      <div key={a.email} className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-300 rounded-lg">
                        <span className="text-xs text-gray-900">{a.email}</span>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => toggleOptional(a.email)}
                            className={`text-[9px] px-1.5 py-0.5 rounded border transition-colors ${a.optional ? 'bg-yellow-50 border-yellow-300 text-yellow-700' : 'bg-blue-100 border-blue-300 text-blue-700'}`}>
                            {a.optional ? 'Optional' : 'Required'}
                          </button>
                          <button onClick={() => removeAttendee(a.email)} className="text-red-400 hover:text-red-600">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Add custom email */}
                    <div className="flex gap-1.5">
                      <input type="email" value={customEmail} onChange={e => setCustomEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addCustomAttendee()}
                        className="flex-1 px-3 py-1.5 text-xs border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white" placeholder="Add email address..." />
                      <button onClick={addCustomAttendee}
                        className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 font-medium">Add</button>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-2 pt-2">
                    <button onClick={() => { setShowForm(false); setEditingMeeting(null); }}
                      className="h-[30px] px-2.5 text-sm border rounded bg-white border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
                    <button onClick={handleSubmit} disabled={creating || !form.date || !form.title || !form.startTime || !form.endTime}
                      className="h-[30px] px-2.5 text-sm border rounded bg-emerald-50 border-emerald-300 text-emerald-700 font-medium hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed">
                      {creating ? 'Saving...' : editingMeeting ? 'Update Meeting' : 'Create'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
  );

  if (embedded) return innerCard;
  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999 }} className="bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={onClose}>
      {innerCard}
    </div>,
    document.body
  );
};

export default MeetingScheduler;
